import axios from 'axios';
import { message } from 'antd';
import store from '../redux/store';
import { logout } from '../redux/Authentication';

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

// Send the httpOnly cookie on every request automatically.
axios.defaults.withCredentials = true;

// 401 / 423 → wipe session + redirect to /login (suppressed on public pages —
// /login keeps its own bad-creds error, /questionnaire is customer-facing and
// unauthenticated, so a stray 401 there must not bounce the visitor).
// 403 → show permission-denied toast with the required permission key.
// `error.handledByInterceptor` lets caller .catch blocks skip their own toasts.
let redirecting = false;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const data   = error?.response?.data;
    const url    = error?.config?.url || '';

    // Only treat /api calls as auth-sensitive.
    const isApiCall = url.includes('/api/');
    if (!isApiCall) return Promise.reject(error);

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const onPublicPage =
      path.startsWith('/login') ||
      path.startsWith('/questionnaire') ||
      path.startsWith('/tracking-failures');

    if (status === 401 || status === 423) {
      error.handledByInterceptor = true;
      if (!onPublicPage && !redirecting) {
        redirecting = true;
        // Ask the server to clear the httpOnly cookie.
        axios.post(`${BASE_URL}/api/auth/logout`).catch(() => {});
        try { localStorage.removeItem('reduxState'); } catch (_) {}
        store.dispatch(logout());
        const msg =
          status === 423
            ? 'Your account is locked. Contact your administrator.'
            : 'Your session has expired. Please log in again.';
        message.error(msg);
        setTimeout(() => {
          window.location.href = '/login';
          redirecting = false;
        }, 200);
      }
    } else if (status === 403) {
      error.handledByInterceptor = true;
      const required = data?.required || data?.requiredAny || data?.requiredAll;
      const requiredStr = Array.isArray(required) ? required.join(', ') : required;
      message.error(
        requiredStr
          ? `Permission denied — you need: ${requiredStr}`
          : "You don't have permission to do this.",
      );
    }

    return Promise.reject(error);
  },
);

async function get(endpoint) {
  const url = `${BASE_URL}/${endpoint}`;
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
}

async function post(endpoint, body, isMultipart = false) {
  const url = `${BASE_URL}/${endpoint}`;
  const config = isMultipart
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {};
  try {
    const response = await axios.post(url, body, config);
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
}

async function put(endpoint, body) {
  const url = `${BASE_URL}/${endpoint}`;
  try {
    const response = await axios.put(url, body);
    return response.data;
  } catch (error) {
    console.error('Error updating data:', error);
    throw error;
  }
}

async function patch(endpoint, body) {
  const url = `${BASE_URL}/${endpoint}`;
  try {
    const response = await axios.patch(url, body);
    return response.data;
  } catch (error) {
    console.error('Error updating data:', error);
    throw error;
  }
}

async function del(endpoint, id) {
  const url = id ? `${BASE_URL}/${endpoint}/${id}` : `${BASE_URL}/${endpoint}`;
  try {
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    console.error('Error deleting data:', error);
    throw error;
  }
}

// postFile is preserved as a convenience alias — equivalent to post(endpoint, body, true)
async function postFile(endpoint, body) {
  return post(endpoint, body, true);
}

export { get, post, put, patch, del, postFile };
